import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Avatar,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Search,
  WarningAmber,
  EventAvailable,
  PhotoCamera,
  UploadFile,
  Visibility,
  Person,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { patientService, authService } from '../../services/api';
import TopBar from '../../components/TopBar';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
const MAX_FILE_SIZE_BYTES = 50 * 1024; // 50 KB

const PatientManagement = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phoneNumber: '',
    email: '',
    address: '',
    bloodGroup: '',
    emergencyContact: '',
    lmp: '',
    edd: '',
    prescriptionLang: 'en',
    aadharNumber: '',
    photoUrl: '',
    aadharFileUrl: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [aadharFile, setAadharFile] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [conflictInfo, setConflictInfo] = useState(null);    // 409 payload
  const [pendingPayload, setPendingPayload] = useState(null); // saved for force-create

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const response = await patientService.getAllPatients();
      setPatients(response.data);
    } catch (err) {
      setError('Failed to load patients');
    }
  };

  const resetForm = () => ({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phoneNumber: '',
    email: '',
    address: '',
    bloodGroup: '',
    emergencyContact: '',
    lmp: '',
    edd: '',
    prescriptionLang: 'en',
    aadharNumber: '',
    photoUrl: '',
    aadharFileUrl: '',
  });

  const handleOpenDialog = (patient = null) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : '',
        gender: patient.gender || '',
        phoneNumber: patient.phoneNumber || '',
        email: patient.email || '',
        address: patient.address || '',
        bloodGroup: patient.bloodGroup || '',
        emergencyContact: patient.emergencyContact || '',
        lmp: patient.lmp ? patient.lmp.split('T')[0] : '',
        edd: patient.edd ? patient.edd.split('T')[0] : '',
        prescriptionLang: patient.marathiPrescription ? 'mr' : patient.hindiPrescription ? 'hi' : 'en',
        aadharNumber: patient.aadharNumber || '',
        photoUrl: patient.photoUrl || '',
        aadharFileUrl: patient.aadharFileUrl || '',
      });
      setPhotoPreview(patient.photoUrl ? `${API_BASE}/${patient.photoUrl}` : '');
    } else {
      setEditingPatient(null);
      setFormData(resetForm());
      setPhotoPreview('');
    }
    setPhotoFile(null);
    setAadharFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPatient(null);
    setFormData(resetForm());
    setPhotoFile(null);
    setPhotoPreview('');
    setAadharFile(null);
    setConflictInfo(null);
    setPendingPayload(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('Photo size must not exceed 50 KB');
      setTimeout(() => setError(''), 3000);
      e.target.value = '';
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = async () => {
    if (editingPatient && formData.photoUrl && !photoFile) {
      try {
        await patientService.deletePatientPhoto(editingPatient.id);
      } catch (err) {
        setError('Failed to remove photo');
        setTimeout(() => setError(''), 3000);
        return;
      }
    }
    setPhotoFile(null);
    setPhotoPreview('');
    setFormData((prev) => ({ ...prev, photoUrl: '' }));
  };

  const handleAadharFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('Aadhar card copy size must not exceed 50 KB');
      setTimeout(() => setError(''), 3000);
      e.target.value = '';
      return;
    }
    setAadharFile(file);
  };

  const handleRemoveAadharFile = async () => {
    if (editingPatient && formData.aadharFileUrl && !aadharFile) {
      try {
        await patientService.deletePatientAadhar(editingPatient.id);
      } catch (err) {
        setError('Failed to remove Aadhar document');
        setTimeout(() => setError(''), 3000);
        return;
      }
    }
    setAadharFile(null);
    setFormData((prev) => ({ ...prev, aadharFileUrl: '' }));
  };

  // Upload selected photo/Aadhar files for a (newly created or existing) patient
  const uploadExtras = async (patientId) => {
    if (photoFile) {
      const fd = new FormData();
      fd.append('file', photoFile);
      await patientService.uploadPatientPhoto(patientId, fd);
    }
    if (aadharFile) {
      const fd = new FormData();
      fd.append('file', aadharFile);
      await patientService.uploadPatientAadhar(patientId, fd);
    }
  };

  const calculateEDD = (lmpValue) => {
    if (!lmpValue) return '';
    const lmpDate = new Date(lmpValue);
    lmpDate.setDate(lmpDate.getDate() + 280);
    return lmpDate.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    if (name === 'lmp') {
      updated.edd = calculateEDD(value);
    }
    setFormData(updated);
  };

  const buildPayload = () => {
    const { prescriptionLang, photoUrl, aadharFileUrl, ...rest } = formData;
    const payload = { ...rest };
    payload.hindiPrescription = prescriptionLang === 'hi';
    payload.marathiPrescription = prescriptionLang === 'mr';
    if (!payload.lmp) payload.lmp = null;
    if (!payload.edd) payload.edd = null;
    if (!payload.aadharNumber) payload.aadharNumber = null;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();

      if (editingPatient) {
        payload.id = editingPatient.id;
        await patientService.updatePatient(editingPatient.id, payload);
        await uploadExtras(editingPatient.id);
        setSuccess('Patient updated successfully!');
        handleCloseDialog();
        loadPatients();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const response = await patientService.createPatient(payload);
        const newId = response.data?.id;
        if (newId) await uploadExtras(newId);
        setSuccess('Patient added successfully!');
        handleCloseDialog();
        loadPatients();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        // Duplicate found — store conflict info and show confirmation dialog
        setPendingPayload(buildPayload());
        setConflictInfo(err.response.data);
      } else {
        setError(err.response?.data?.message || 'Failed to save patient');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // User chose to register anyway despite the duplicate warning
  const handleForceCreate = async () => {
    try {
      const response = await patientService.createPatient(pendingPayload, true);
      const newId = response.data?.id;
      if (newId) await uploadExtras(newId);
      setConflictInfo(null);
      setPendingPayload(null);
      setSuccess('Patient registered successfully!');
      handleCloseDialog();
      loadPatients();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save patient');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBookAppointment = () => {
    const patientId = conflictInfo?.existingPatient?.id;
    setConflictInfo(null);
    setPendingPayload(null);
    handleCloseDialog();
    if (patientId) {
      navigate('/appointments', { state: { openNewWithPatientId: patientId } });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await patientService.deletePatient(id);
        setSuccess('Patient deleted successfully!');
        loadPatients();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete patient');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Filter patients based on search text
  const filteredPatients = patients.filter((patient) => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    
    // Search across all relevant fields
    return (
      patient.id?.toString().includes(searchLower) ||
      patient.firstName?.toLowerCase().includes(searchLower) ||
      patient.lastName?.toLowerCase().includes(searchLower) ||
      patient.gender?.toLowerCase().includes(searchLower) ||
      patient.phoneNumber?.toLowerCase().includes(searchLower) ||
      patient.email?.toLowerCase().includes(searchLower) ||
      patient.bloodGroup?.toLowerCase().includes(searchLower) ||
      patient.address?.toLowerCase().includes(searchLower) ||
      patient.emergencyContact?.toLowerCase().includes(searchLower) ||
      patient.age?.toString().includes(searchLower)
    );
  });

  const columns = [
    { field: 'id', headerName: 'ID', width: 55 },
    { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 100 },
    { field: 'lastName', headerName: 'Last Name', flex: 1, minWidth: 100 },
    {
      field: 'age',
      headerName: 'Age',
      width: 55,
      valueGetter: (params) => params.row.age || '-',
    },
    { field: 'gender', headerName: 'Gender', width: 75 },
    { field: 'phoneNumber', headerName: 'Phone', width: 120 },
    { field: 'bloodGroup', headerName: 'Blood Grp', width: 85 },
    { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 130 },
    {
      field: 'lmp',
      headerName: 'LMP',
      width: 100,
      valueGetter: (params) =>
        params.row.lmp ? new Date(params.row.lmp).toLocaleDateString('en-IN') : '-',
    },
    {
      field: 'edd',
      headerName: 'EDD',
      width: 100,
      valueGetter: (params) =>
        params.row.edd ? new Date(params.row.edd).toLocaleDateString('en-IN') : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)}>
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Patient Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              All Patients
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              }}
            >
              New Patient
            </Button>
          </Box>

          {/* Search Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search patients by name, phone, email, blood group, etc..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search sx={{ color: 'action.active', mr: 1 }} />
                ),
              }}
              variant="outlined"
              size="medium"
            />
          </Box>

          <DataGrid
            rows={filteredPatients}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      {/* Add / Edit Patient Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Avatar
                    variant="rounded"
                    src={photoPreview || undefined}
                    imgProps={{ style: { objectFit: 'cover' } }}
                    sx={{ width: 110, height: 140, bgcolor: 'grey.300' }}
                  >
                    {!photoPreview && <Person sx={{ fontSize: 56 }} />}
                  </Avatar>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" component="label" startIcon={<PhotoCamera />}>
                      {photoPreview ? 'Change' : 'Photo'}
                      <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                    </Button>
                    {photoPreview && (
                      <IconButton size="small" color="error" onClick={handleRemovePhoto}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Max size 50 KB
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Blood Group"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Blood Group</MenuItem>
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Aadhar Card Number"
                  name="aadharNumber"
                  value={formData.aadharNumber}
                  onChange={handleChange}
                  inputProps={{ maxLength: 14 }}
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button variant="outlined" size="small" component="label" startIcon={<UploadFile />}>
                      {formData.aadharFileUrl || aadharFile ? 'Replace Aadhar Copy' : 'Upload Aadhar Copy'}
                      <input type="file" hidden accept="image/*,application/pdf" onChange={handleAadharFileChange} />
                    </Button>
                    {aadharFile && (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                        {aadharFile.name}
                      </Typography>
                    )}
                    {formData.aadharFileUrl && !aadharFile && (
                      <IconButton
                        size="small"
                        onClick={() => window.open(`${API_BASE}/${formData.aadharFileUrl}`, '_blank')}
                        title="View uploaded Aadhar copy"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    )}
                    {(aadharFile || formData.aadharFileUrl) && (
                      <IconButton size="small" color="error" onClick={handleRemoveAadharFile} title="Remove">
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Max size 50 KB
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LMP (Last Menstrual Period)"
                  name="lmp"
                  type="date"
                  value={formData.lmp}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={formData.gender === 'Male'}
                  helperText={formData.gender === 'Male' ? 'Not applicable for Male' : 'Optional - select date from calendar'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="EDD (Estimated Delivery Date)"
                  name="edd"
                  type="date"
                  value={formData.edd}
                  InputLabelProps={{ shrink: true }}
                  helperText="Auto-calculated from LMP (LMP + 280 days)"
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel component="legend" sx={{ mb: 0.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                  Prescription Language
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.prescriptionLang}
                  onChange={(e) => setFormData(prev => ({ ...prev, prescriptionLang: e.target.value }))}
                >
                  <FormControlLabel value="en" control={<Radio />} label="English" />
                  <FormControlLabel value="hi" control={<Radio />} label="Hindi" />
                  <FormControlLabel value="mr" control={<Radio />} label="Marathi" />
                </RadioGroup>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPatient ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Duplicate Conflict Confirmation Dialog */}
      <Dialog open={!!conflictInfo} onClose={() => { setConflictInfo(null); setPendingPayload(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningAmber color="warning" />
          Duplicate Patient Detected
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {conflictInfo?.message}
          </Alert>
          {conflictInfo?.existingPatient && (
            <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Existing Patient Record</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {conflictInfo.existingPatient.firstName} {conflictInfo.existingPatient.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Gender</Typography>
                  <Typography variant="body2">{conflictInfo.existingPatient.gender}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body2">
                    {conflictInfo.existingPatient.dateOfBirth
                      ? new Date(conflictInfo.existingPatient.dateOfBirth).toLocaleDateString('en-IN')
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2">{conflictInfo.existingPatient.phoneNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Patient ID</Typography>
                  <Typography variant="body2">#{conflictInfo.existingPatient.id}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" mt={2}>
            What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setConflictInfo(null); setPendingPayload(null); }}>
            Go Back &amp; Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EventAvailable />}
            onClick={handleBookAppointment}
          >
            Book Appointment
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Add />}
            onClick={handleForceCreate}
          >
            Register Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PatientManagement;
